import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { usd, fmtDate } from "@/lib/constants";
import { buildPropertyResearchLinks, VISUALS_SITES, normalizeAddressClient } from "@/lib/research";
import { VERIF_LABELS, VERIF_BADGE } from "@/lib/prospectConstants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AreaField } from "@/components/common";
import { Star, Plus, Calculator, RefreshCw, ExternalLink, MapPin, User, AlertTriangle, DollarSign, ShieldCheck, Camera, StickyNote, PhoneCall } from "lucide-react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="border border-border rounded-md p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</span>
      </div>
      {children}
    </div>
  );
}

function SourceTag({ src }) {
  if (!src) return null;
  return (
    <span className="block text-[10px] text-muted-foreground mt-0.5">
      {src.source}{src.estimated ? " · Estimated" : ""}{src.verified_at ? ` · Last verified: ${fmtDate ? fmtDate(src.verified_at) : src.verified_at.slice(0, 10)}` : ""}
      {src.url && <a href={src.url} target="_blank" rel="noopener noreferrer" className="ml-1 underline hover:text-accent">source</a>}
    </span>
  );
}

function FieldRow({ label, value, source, estimated, testid }) {
  return (
    <div data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}{estimated ? <Badge variant="outline" className="ml-1.5 text-[9px] py-0 bg-amber-50 text-amber-700 border-amber-200">ESTIMATED</Badge> : null}</span>
      <div className="text-sm font-medium">{value ?? "—"}</div>
      <SourceTag src={source} />
    </div>
  );
}

export default function ProspectDetail({ prospect, open, onOpenChange, onUpdated, svEnabled }) {
  const [p, setP] = useState(prospect);
  const [notes, setNotes] = useState({ prospect_notes: "", research_notes: "", internal_notes: "" });
  const [busy, setBusy] = useState("");
  const [gisBusy, setGisBusy] = useState(false);
  const [svFailed, setSvFailed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    setP(prospect); setSvFailed(false);
    setNotes({ prospect_notes: prospect?.prospect_notes || "", research_notes: prospect?.research_notes || "", internal_notes: prospect?.internal_notes || "" });
  }, [prospect]);

  if (!p) return null;
  const fs = p.field_sources || {};
  const fullAddr = normalizeAddressClient(p);
  const links = buildPropertyResearchLinks(fullAddr);
  const sources = [...new Set(Object.values(fs).map((s) => s.source).filter(Boolean))];

  const patch = async (body, okMsg) => {
    try {
      const { data } = await api.patch(`/prospects/${p.id}`, body);
      setP(data); onUpdated?.(data);
      if (okMsg) toast.success(okMsg);
      return data;
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Update failed"); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post(`/prospects/${p.id}/refresh`);
      if (data.prospect) { setP(data.prospect); onUpdated?.(data.prospect); }
      toast.success(data.api_call_made ? "Property data refreshed from provider" : "Loaded latest cached data");
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(""); }
  };

  const toLead = async (analyze) => {
    setBusy(analyze ? "analyze" : "lead");
    try {
      const { data } = await api.post(`/prospects/${p.id}/to-lead`);
      toast.success(data.existing ? "Opening existing lead" : "Lead created from prospect");
      nav(analyze ? `/analyzer?lead_id=${data.lead_id}` : `/leads/${data.lead_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create lead"); }
    finally { setBusy(""); }
  };

  const openCountyGis = async () => {
    if (!p.state || !p.county) return toast.message("County GIS research unavailable for this jurisdiction.");
    setGisBusy(true);
    try {
      const { data } = await api.get("/county-offices/match", { params: { state: p.state, county: p.county } });
      const url = data?.gis_map_url || data?.property_search_url;
      if (url) window.open(url, "_blank", "noopener");
      else toast.message("County GIS research unavailable for this jurisdiction.");
    } catch { toast.message("County GIS research unavailable for this jurisdiction."); }
    finally { setGisBusy(false); }
  };

  const skipTrace = async () => {
    setBusy("trace");
    try {
      const { data } = await api.post(`/prospects/${p.id}/skip-trace`);
      if (data.prospect) { setP(data.prospect); onUpdated?.(data.prospect); }
      if (data.mocked) toast.message(data.note);
      else if (data.no_match) toast.message("No contact information found.");
      else toast.success("Skip trace completed.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : "Skip trace could not be completed.");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="prospect-detail">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2" data-testid="prospect-detail-title">
            {p.property_address}{p.unit ? ` ${p.unit}` : ""}, {p.city}, {p.state} {p.zip_code}
            <Badge variant="outline" className={`${VERIF_BADGE[p.verification_status] || VERIF_BADGE.unverified} text-[10px]`}>{VERIF_LABELS[p.verification_status] || "Unverified"}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid="prospect-detail-actions">
          <Button size="sm" variant={p.starred ? "default" : "outline"} onClick={() => patch({ starred: !p.starred }, p.starred ? "Removed star" : "Prospect starred")} data-testid="detail-star">
            <Star className={`w-3.5 h-3.5 mr-1.5 ${p.starred ? "fill-current" : ""}`} />{p.starred ? "Starred" : "Save / Star"}</Button>
          {p.lead_id ? (
            <Button size="sm" variant="outline" onClick={() => nav(`/leads/${p.lead_id}`)} data-testid="detail-open-lead">Open Lead</Button>
          ) : (
            <Button size="sm" variant="outline" disabled={busy === "lead"} onClick={() => toLead(false)} data-testid="detail-to-lead">
              <Plus className="w-3.5 h-3.5 mr-1.5" />{busy === "lead" ? "Adding…" : "Add to Leads"}</Button>
          )}
          <Button size="sm" variant="outline" disabled={busy === "analyze"} onClick={() => toLead(true)} data-testid="detail-analyze">
            <Calculator className="w-3.5 h-3.5 mr-1.5" />{busy === "analyze" ? "Opening…" : "Analyze"}</Button>
          <Button size="sm" variant="outline" disabled={busy === "trace"} onClick={skipTrace} data-testid="detail-skip-trace">
            <PhoneCall className="w-3.5 h-3.5 mr-1.5" />{busy === "trace" ? "Skip Tracing…" : "Skip Trace"}</Button>
          <Button size="sm" variant="ghost" disabled={busy === "refresh"} onClick={refresh} data-testid="detail-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy === "refresh" ? "animate-spin" : ""}`} />Refresh Property Data</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Section icon={MapPin} title="Property" testid="section-property">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Property Address" value={`${p.property_address}${p.unit ? ` ${p.unit}` : ""}`} source={fs.property_address} testid="f-address" />
              <FieldRow label="City" value={p.city} testid="f-city" />
              <FieldRow label="County" value={p.county} source={fs.county} testid="f-county" />
              <FieldRow label="State" value={p.state} testid="f-state" />
              <FieldRow label="ZIP" value={p.zip_code} testid="f-zip" />
              <FieldRow label="APN" value={p.apn} source={fs.apn} testid="f-apn" />
              <FieldRow label="Case Number" value={p.case_number} source={fs.case_number} testid="f-case" />
              <FieldRow label="Property Type" value={p.property_type} source={fs.property_type} testid="f-type" />
            </div>
          </Section>

          <Section icon={User} title="Owner" testid="section-owner">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Owner Name" value={p.owner_name} source={fs.owner_name} testid="f-owner" />
              <FieldRow label="Owner Mailing Address" value={p.owner_mailing_address} source={fs.owner_mailing_address} testid="f-mailing" />
              <FieldRow label="Public Phone" value={p.public_phone} source={fs.public_phone} testid="f-phone" />
              <FieldRow label="Public Email" value={p.public_email} source={fs.public_email} testid="f-email" />
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Owner mailing address may differ from the property address above.</p>
            {p.skip_trace_results && (p.skip_trace_results.phones?.length || p.skip_trace_results.emails?.length) ? (
              <div className="mt-2 border-t border-border pt-2" data-testid="skip-trace-results">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Skip Trace Results — Source: {p.skip_trace_results.provider} · {String(p.skip_trace_results.traced_at || "").slice(0, 10)}</span>
                {(p.skip_trace_results.phones || []).map((ph) => <div key={ph} className="text-sm font-mono-nums">{ph}</div>)}
                {(p.skip_trace_results.emails || []).map((em) => <div key={em} className="text-sm">{em}</div>)}
              </div>
            ) : null}
          </Section>

          <Section icon={AlertTriangle} title="Distress & Auction" testid="section-distress">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Distress Type" value={p.distress_type} source={fs.distress_type} testid="f-distress" />
              <FieldRow label="Foreclosure Status" value={p.foreclosure_status} source={fs.foreclosure_status} testid="f-fc-status" />
              <FieldRow label="Case Status" value={p.case_status} source={fs.case_status} testid="f-case-status" />
              <FieldRow label="Default Date" value={p.default_date} source={fs.default_date} testid="f-default-date" />
              <FieldRow label="Auction Status" value={p.auction_status} source={fs.auction_status} testid="f-auction-status" />
              <FieldRow label="Auction Date" value={p.auction_date} source={fs.auction_date} testid="f-auction-date" />
            </div>
          </Section>

          <Section icon={DollarSign} title="Financial" testid="section-financial">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Property Value" value={p.estimated_value != null ? usd(p.estimated_value) : null} source={fs.estimated_value} estimated testid="f-value" />
              <FieldRow label="Equity" value={p.estimated_equity != null ? usd(p.estimated_equity) : null} source={fs.estimated_equity} estimated testid="f-equity" />
              <FieldRow label="Mortgage Balance" value={p.estimated_mortgage_balance != null ? usd(p.estimated_mortgage_balance) : null} source={fs.estimated_mortgage_balance} estimated testid="f-mortgage" />
              <FieldRow label="Loan Amount" value={p.estimated_loan_amount != null ? usd(p.estimated_loan_amount) : null} source={fs.estimated_loan_amount} estimated testid="f-loan" />
              <FieldRow label="Rent" value={p.estimated_rent != null ? usd(p.estimated_rent) : null} source={fs.estimated_rent} estimated testid="f-rent" />
              <FieldRow label="Tax Amount" value={p.tax_amount != null ? usd(p.tax_amount) : null} source={fs.tax_amount} testid="f-tax" />
            </div>
            {p.equity_source && <p className="text-[10px] text-muted-foreground mt-2" data-testid="equity-source">Equity source: {p.equity_source}</p>}
            <p className="text-[10px] text-muted-foreground mt-1">Estimated values are not verified facts.</p>
          </Section>

          <Section icon={ShieldCheck} title="Verification" testid="section-verification">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Verification Status" value={VERIF_LABELS[p.verification_status] || "Unverified"} testid="f-verif-status" />
              <FieldRow label="Source" value={sources.length ? sources.join(", ") : "None recorded"} testid="f-verif-source" />
              <FieldRow label="Source Last Checked" value={p.source_last_checked ? p.source_last_checked.slice(0, 10) : null} testid="f-last-checked" />
              <FieldRow label="Data Last Updated" value={p.data_last_updated ? p.data_last_updated.slice(0, 10) : (p.updated_at || "").slice(0, 10)} testid="f-last-updated" />
            </div>
          </Section>

          <Section icon={Camera} title="Property Photo" testid="section-photo">
            {svEnabled && !svFailed ? (
              <img src={`${API}/streetview?address=${encodeURIComponent(fullAddr)}&size=640x400`} alt="Street view"
                loading="lazy" className="w-full max-h-56 object-cover rounded border border-border"
                onError={() => setSvFailed(true)} data-testid="detail-streetview" />
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-photo">No Street View imagery available</p>
            )}
          </Section>
        </div>

        <Section icon={ExternalLink} title="Property Visuals & Research" testid="section-research">
          <div className="text-xs text-muted-foreground mb-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="research-property-info">
            <span className="font-medium text-foreground">{p.property_address}{p.unit ? ` ${p.unit}` : ""}</span>
            <span>{p.city || "—"}</span>
            <span>{p.county ? `${p.county} County` : "County —"}</span>
            <span>{p.state || "—"} {p.zip_code || ""}</span>
            <span>APN: {p.apn || "—"}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {VISUALS_SITES.map(([key, label]) => (
              <a key={key} href={links[key]} target="_blank" rel="noopener noreferrer" data-testid={`research-${key}`}
                className="inline-flex items-center gap-1.5 text-sm border border-border rounded-full px-3 py-1.5 hover:border-accent hover:text-accent transition-colors">
                {label}<ExternalLink className="w-3 h-3" /></a>
            ))}
            <button onClick={openCountyGis} disabled={gisBusy} data-testid="research-county-gis"
              className="inline-flex items-center gap-1.5 text-sm border border-border rounded-full px-3 py-1.5 hover:border-accent hover:text-accent transition-colors disabled:opacity-50">
              {gisBusy ? "Opening…" : "County / GIS"}<ExternalLink className="w-3 h-3" /></button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Links are generated from the property address — opening one never uses AI or paid APIs.</p>
        </Section>

        <Section icon={StickyNote} title="Notes" testid="section-notes">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <AreaField label="Prospect Notes" value={notes.prospect_notes} onChange={(v) => setNotes((n) => ({ ...n, prospect_notes: v }))} testid="notes-prospect" />
            <AreaField label="Research Notes" value={notes.research_notes} onChange={(v) => setNotes((n) => ({ ...n, research_notes: v }))} testid="notes-research" />
            <AreaField label="Internal Notes" value={notes.internal_notes} onChange={(v) => setNotes((n) => ({ ...n, internal_notes: v }))} testid="notes-internal" />
          </div>
          <Button size="sm" className="mt-3" onClick={() => patch(notes, "Notes saved")} data-testid="notes-save">Save Notes</Button>
        </Section>
      </DialogContent>
    </Dialog>
  );
}
